import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Polls the "unquoted" quote count every 30s so the sidebar Quotes tab
 * can show a red badge when someone's enquiry hasn't been priced yet.
 * Same shared-poll pattern as `useUnviewedLeads` — one HTTP request per
 * app instance regardless of how many components subscribe.
 */
const listeners = new Set();
let sharedCount = 0;
let pollTimer = null;
let inflight = false;
let activeToken = null;

function broadcast(next) {
  sharedCount = next;
  listeners.forEach((cb) => cb(next));
}

async function fetchCountOnce() {
  if (inflight) return;
  inflight = true;
  try {
    const { data } = await api.get("/quotes/unquoted-count");
    broadcast(Number(data?.count || 0));
  } catch (_) {
    /* keep previous count on transient error */
  } finally {
    inflight = false;
  }
}

function startPolling() {
  if (pollTimer) return;
  fetchCountOnce();
  pollTimer = setInterval(fetchCountOnce, 30_000);
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  sharedCount = 0;
  activeToken = null;
}

export function useUnquotedQuotes() {
  const { user } = useAuth();
  const [count, setCount] = useState(sharedCount);

  useEffect(() => {
    const eligible = user && (user.role === "admin" || user.role === "office");
    if (!eligible) {
      if (activeToken) stopPolling();
      return;
    }
    const token = user.id;
    if (activeToken && activeToken !== token) {
      stopPolling();
    }
    activeToken = token;
    listeners.add(setCount);
    setCount(sharedCount);
    startPolling();
    return () => {
      listeners.delete(setCount);
      if (listeners.size === 0) stopPolling();
    };
  }, [user?.id, user?.role]);

  return { count, refresh: fetchCountOnce };
}
